import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";

const SCOPES = [
  { key: "collections", label: "Waste collections", warn: "All logged kg / L across every client" },
  { key: "pickups",     label: "Pickup requests",   warn: "Every requested & scheduled pickup" },
  { key: "trips",       label: "Trips & routes",    warn: "Driver trip records + stops + photos" },
  { key: "invoices",    label: "Invoices",          warn: "Issued invoices (draft + sent)" },
  { key: "photos",      label: "Site photos",       warn: "Standalone photo uploads (not trip photos)" },
  { key: "driver_gps",  label: "Driver GPS trails", warn: "Live & historical driver locations" },
];

export default function Settings() {
  const { user } = useAuth();
  const [depot, setDepot] = useState(null);
  const [scopes, setScopes] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user?.role === "admin") api.get("/settings/depot").then((r) => setDepot(r.data)); }, [user]);
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const toggle = (k) => setScopes((s) => ({ ...s, [k]: !s[k] }));
  const anyPicked = Object.values(scopes).some(Boolean);

  const runWipe = async () => {
    const picked = Object.keys(scopes).filter((k) => scopes[k]);
    if (!picked.length) { toast.error("Tick at least one scope."); return; }
    if (confirm !== "WIPE") { toast.error("Type WIPE exactly to confirm."); return; }
    if (!window.confirm(`Delete: ${picked.join(", ")}. This cannot be undone. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/reset-transactional", { confirm: "WIPE", scopes: picked });
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${total} records across ${picked.length} scope(s).`);
      setScopes({}); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wipe failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Settings" subtitle="Depot config and data lifecycle controls." />
      <div className="px-8 lg:px-12 py-8 max-w-3xl space-y-8">
        {depot && (
          <div className="hbt-card p-6">
            <div className="hbt-label mb-3">Depot</div>
            <div className="text-sm">{depot.name || "HBT Matsapha"}{depot.address ? ` · ${depot.address}` : ""}</div>
            <div className="text-xs text-neutral-500 mt-1">Coming soon: edit depot address, timezone and rate cards.</div>
          </div>
        )}

        <div className="hbt-card p-6 border-l-4" style={{ borderLeftColor: "var(--terracotta, #C85A3C)" }} data-testid="danger-zone-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--terracotta, #C85A3C)" }} />
            <div className="font-display text-xl font-semibold">Danger zone · granular data reset</div>
          </div>
          <p className="text-sm text-neutral-600 mb-4">Tick only the scopes you want to remove. Clients, sites, users, licenses and the waste catalog are never touched — you always keep your structural data.</p>
          <div className="space-y-2 mb-5">
            {SCOPES.map((s) => (
              <label key={s.key} className="flex items-start gap-3 p-3 rounded border cursor-pointer" style={{ borderColor: scopes[s.key] ? "var(--terracotta, #C85A3C)" : "rgba(10,10,10,0.10)", background: scopes[s.key] ? "rgba(200,90,60,0.06)" : "transparent" }}>
                <input type="checkbox" checked={!!scopes[s.key]} onChange={() => toggle(s.key)} data-testid={`wipe-scope-${s.key}`} />
                <div>
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="text-xs text-neutral-500">{s.warn}</div>
                </div>
              </label>
            ))}
          </div>
          <label className="hbt-label block mb-2">Type <span className="font-mono">WIPE</span> to confirm</label>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="hbt-input font-mono mb-4" data-testid="wipe-confirm-input" />
          <button
            onClick={runWipe}
            disabled={busy || !anyPicked || confirm !== "WIPE"}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            style={{ background: "var(--terracotta, #C85A3C)" }}
            data-testid="wipe-run-btn"
          >
            <Trash2 className="w-4 h-4" /> {busy ? "Wiping…" : `Wipe selected (${Object.values(scopes).filter(Boolean).length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
